/**
 * Who a Bedrock call was for, and what it cost.
 *
 * THE PROBLEM THIS SOLVES
 * -----------------------
 * Carrier shares an AWS account with another product, and every Bedrock call
 * used to go out with nothing on it: no product, no org, no sub-account, and
 * the token counts in the response were read by nobody. So "how much did
 * Carrier spend on AI" and "which customer spent it" were both unanswerable,
 * and a CloudWatch invocation count multiplied by an assumed average was the
 * best available answer.
 *
 * TWO LAYERS, AND THEY ARE NOT INTERCHANGEABLE
 * --------------------------------------------
 * 1. AWS-native, invoice-accurate: an application inference profile whose TAGS
 *    are attached to the billing record for each request. Those reach Cost
 *    Explorer and CUR. Set BEDROCK_MODEL_ID to the profile ARN and the tags
 *    apply with no code change, which is why this module does not build them.
 *
 * 2. requestMetadata, this module: reaches model invocation LOGS only. AWS is
 *    explicit that it "is not delivered as an AWS cost allocation tag and does
 *    not appear in AWS Cost Explorer or CUR". Anything costed from it is an
 *    estimate we own, computed from token counts against a rate card we
 *    maintain. Useful, and not the bill.
 *
 * Finest grain AWS bills at is per usage type per day, so per-request and
 * per-sub-account attribution can only ever be layer 2. Reconcile monthly:
 * summed estimates against the profile-tagged CUR total for the same org.
 * Drift means metadata is missing somewhere.
 *
 * THE SIGV4 TRAP
 * --------------
 * `X-Amzn-Bedrock-Request-Metadata` MUST appear in the signed header list.
 * A request that sends it unsigned is rejected with InvalidSignatureException.
 * The AWS SDKs handle this; aws4fetch signs only what it is told to, so
 * `signQuery`/`allHeaders` has to be set deliberately. Getting this wrong
 * fails 100% of calls, loudly, which is the good case.
 */

/** Who the call is for. Everything is optional so a caller cannot be blocked. */
export interface AiAttribution {
  /** Clerk org id. The billable tenant. */
  orgId?: string;
  /** OCS sub-account id. The finest grain we attribute, and never in the bill. */
  subAccountId?: string;
  /** Which surface asked: chat, insights, a composite name, carrier_ask. */
  surface?: string;
  /** Billing tier at the time of the call, so spend can be read against plan. */
  tier?: string;
}

/**
 * AWS constraints on requestMetadata, from the Converse API reference.
 *
 * Both are hard limits that produce a validation error rather than truncation,
 * so they are enforced here instead of discovered in production.
 */
const MAX_ENTRIES = 16;
const MAX_KEY_LEN = 256;
const MAX_VALUE_LEN = 256;

/**
 * Characters AWS permits in a metadata key or value:
 *   [a-zA-Z0-9\s:_@$#=/+,-.]
 *
 * Clerk org ids (org_3Da…) and OCS numeric ids are already inside this set.
 * Anything outside it is stripped rather than rejected: losing a character of
 * a surface label is a better outcome than failing a paid call over
 * punctuation.
 */
const ALLOWED = /[^a-zA-Z0-9\s:_@$#=/+,\-.]/g;

function clean(value: string, max: number): string {
  return value.replace(ALLOWED, "").slice(0, max);
}

/** The header name. Exported because the signer has to name it explicitly. */
export const REQUEST_METADATA_HEADER = "X-Amzn-Bedrock-Request-Metadata";

/**
 * Build the metadata object, or null when there is nothing worth sending.
 *
 * Null rather than `{}` so the caller can omit the header entirely: an empty
 * header is a signing obligation with no payoff.
 */
export function requestMetadata(attr: AiAttribution): Record<string, string> | null {
  const out: Record<string, string> = {};

  // `product` is what separates Carrier from the other product on this
  // account in the LOGS. The bill separates them by inference profile tag.
  const pairs: Array<[string, string | undefined]> = [
    ["product", "carrier"],
    ["org", attr.orgId],
    ["subaccount", attr.subAccountId],
    ["surface", attr.surface],
    ["tier", attr.tier],
  ];

  for (const [k, v] of pairs) {
    if (v === undefined || v === "") continue;
    const key = clean(k, MAX_KEY_LEN);
    const value = clean(v, MAX_VALUE_LEN);
    if (key && value && Object.keys(out).length < MAX_ENTRIES) out[key] = value;
  }

  return Object.keys(out).length > 0 ? out : null;
}

/** Token counts as Anthropic reports them on Bedrock. */
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  /** Cached prompt read, billed at a tenth of the input rate. */
  cacheReadTokens: number;
  /** Writing the cache, billed above the input rate. */
  cacheWriteTokens: number;
}

export const ZERO_USAGE: TokenUsage = {
  inputTokens: 0,
  outputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
};

/**
 * Pull token counts out of a Bedrock response body or a message_start /
 * message_delta streaming event.
 *
 * Returns zeros rather than throwing. A cost figure that silently reads zero is
 * a worse failure than one that throws, so callers should treat an all-zero
 * usage on a successful call as a bug signal, and `hasUsage` exists to make
 * that check cheap.
 */
export function extractUsage(payload: unknown): TokenUsage {
  if (!payload || typeof payload !== "object") return ZERO_USAGE;

  const root = payload as Record<string, unknown>;
  // Non-streaming puts usage at the root. message_start nests it under
  // `message`. message_delta puts it at the root with output_tokens only.
  const holder =
    (root.usage as Record<string, unknown> | undefined) ??
    ((root.message as Record<string, unknown> | undefined)?.usage as
      | Record<string, unknown>
      | undefined);
  if (!holder) return ZERO_USAGE;

  const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  return {
    inputTokens: num(holder.input_tokens),
    outputTokens: num(holder.output_tokens),
    cacheReadTokens: num(holder.cache_read_input_tokens),
    cacheWriteTokens: num(holder.cache_creation_input_tokens),
  };
}

export function hasUsage(u: TokenUsage): boolean {
  return u.inputTokens + u.outputTokens + u.cacheReadTokens + u.cacheWriteTokens > 0;
}

/** Add two usages. Streaming reports input at start and output at the end. */
export function addUsage(a: TokenUsage, b: TokenUsage): TokenUsage {
  return {
    inputTokens: a.inputTokens + b.inputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
    cacheReadTokens: a.cacheReadTokens + b.cacheReadTokens,
    cacheWriteTokens: a.cacheWriteTokens + b.cacheWriteTokens,
  };
}

/**
 * Haiku 4.5 rates, USD per million tokens.
 *
 * A rate card in code goes stale silently, which is the failure mode to fear
 * here: nobody notices a cost estimate drifting 20% low. It is dated, and the
 * reconciliation against the profile-tagged CUR total is what catches drift.
 * If that reconciliation shows a gap, suspect this constant first.
 *
 * Verified 2026-08-22.
 */
export const HAIKU_RATES = {
  inputPerMTok: 1.0,
  outputPerMTok: 5.0,
  cacheReadPerMTok: 0.1,
  cacheWritePerMTok: 1.25,
  asOf: "2026-08-22",
} as const;

/** Estimated USD for one call. An estimate, by construction. See the header. */
export function estimateCostUsd(u: TokenUsage, rates = HAIKU_RATES): number {
  const perTok = (mtok: number) => mtok / 1_000_000;
  return (
    u.inputTokens * perTok(rates.inputPerMTok) +
    u.outputTokens * perTok(rates.outputPerMTok) +
    u.cacheReadTokens * perTok(rates.cacheReadPerMTok) +
    u.cacheWriteTokens * perTok(rates.cacheWritePerMTok)
  );
}
